/**
 * Flat shipping (flat_rate / flat_rate_range / cart_percentage): the JS twin
 * of the server's ONE rule, api/src/pricing/flat_shipping.py.
 *
 * Bug Intake #549 (https://trello.com/c/HyIbMXmZ). The server prices flat
 * shipping and checkout charges ITS figure. The admin Add New Order cart
 * shows that figure and computes nothing. The storefront cart still shows
 * an estimate before its server quote lands; this is that estimate, held
 * to the server's table (api/tests/fixtures/flat_shipping_parity.json, by
 * utils/__tests__/flat-shipping-cents.spec.js) so the two cannot disagree.
 * Read the server module for the rule; in short:
 *
 *  - The base is the cart's ITEMS subtotal, in whole cents.
 *  - flat_rate: the configured price.
 *  - flat_rate_range: bands {lower_limit, upper_limit ("∞" =
 *    unbounded), flat_rate}, limits inclusive, the LAST matching band wins.
 *  - cart_percentage: {percentage, min_cost, max_cost}, raised to the min
 *    then lowered to the max.
 *  - Money is whole cents, ROUND_HALF_UP on the decimal form.
 *  - null where the config cannot price the cart (no config, malformed, a
 *    cart outside every band, a percentage with no min or max): the cart
 *    shows TBD, never $0 or NaN.
 */
import { halfUpCents, percentOfCentsHalfUp } from "./cartChargeCents";

/** The editor's encoding of an unbounded top band. */
export const UNBOUNDED = "∞";

// A plain decimal: no exponent, no hex, no units. Same pattern as the server.
const PLAIN_DECIMAL = /^\s*-?(\d+(\.\d*)?|\.\d+)\s*$/;

/** `value` as a finite number, or null when it is not one. */
function readNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && PLAIN_DECIMAL.test(value)) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Whole cents (half-up) of a dollar amount, or null. */
function readCents(value) {
  const parsed = readNumber(value);
  return parsed === null ? null : halfUpCents(parsed);
}

/** A non-negative price in whole cents, or null. */
function priceCents(value) {
  const cents = readCents(value);
  return cents !== null && cents >= 0 ? cents : null;
}

function rangeCents(bands, itemsCents) {
  if (!Array.isArray(bands) || bands.length === 0) return null;
  let chosen = null;
  for (const band of bands) {
    if (!band || typeof band !== "object" || Array.isArray(band)) return null;
    const lower = readCents(band.lower_limit);
    const rate = priceCents(band.flat_rate);
    const unbounded = band.upper_limit === UNBOUNDED;
    const upper = unbounded ? null : readCents(band.upper_limit);
    if (lower === null || rate === null || (!unbounded && upper === null)) {
      // One unreadable band makes the whole table untrustworthy.
      return null;
    }
    if (lower <= itemsCents && (unbounded || itemsCents <= upper)) {
      chosen = rate;
    }
  }
  return chosen;
}

function percentageCents(info, itemsCents) {
  if (!info || typeof info !== "object" || Array.isArray(info)) return null;
  const pct = readNumber(info.percentage);
  const low = priceCents(info.min_cost);
  const high = priceCents(info.max_cost);
  if (pct === null || pct < 0 || low === null || high === null) return null;
  const share = percentOfCentsHalfUp(itemsCents, pct);
  return Math.min(Math.max(share, low), high);
}

/**
 * The flat shipping, in whole cents, for a cart of `itemsCents`; null when
 * `type` is not a flat type or its config cannot price this cart.
 */
export function flatShippingCents(type, info, itemsCents) {
  if (type === "flat_rate") return priceCents(info);
  if (type === "flat_rate_range") return rangeCents(info, itemsCents);
  if (type === "cart_percentage") return percentageCents(info, itemsCents);
  return null;
}

/**
 * `campaign`'s flat shipping for an items subtotal in dollars, in dollars;
 * null when it cannot be priced.
 */
export function flatShippingCost(campaign, itemsDollars) {
  const itemsCents = readCents(itemsDollars);
  if (!campaign || itemsCents === null) return null;
  const cents = flatShippingCents(
    campaign.shipping_cost_type,
    campaign.shipping_cost_info,
    itemsCents
  );
  return cents === null ? null : cents / 100;
}
